import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined"
import VoicemailOutlinedIcon from "@mui/icons-material/VoicemailOutlined"
import Alert from "@mui/material/Alert"
import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import ListItemButton from "@mui/material/ListItemButton"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import { format, isThisYear, isToday, isYesterday } from "date-fns"
import { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Link as RouterLink, useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import { FOOTER_HEIGHT } from "../../../common/components/Footer"
import Row from "../../../common/components/Row"
import { useIsMobile } from "../../../common/utils"
import { isConcierge } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import InboxChannelToggle from "./InboxChannelToggle"
import InboxContactChip from "./InboxContactChip"
import ProfileColumn from "./ProfileColumn"
import VoicemailRecordingPlayer from "./VoicemailRecordingPlayer"
import { inboxContactDetailsPath } from "./inboxContactPath"

const VOICEMAIL_LIST_POLL_MS = 60000

function firstApiErrorMessage(data, fallback) {
  if (!data || typeof data !== "object") {
    return fallback
  }
  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail
  }
  for (const key of ["non_field_errors", "detail"]) {
    const value = data[key]
    if (typeof value === "string" && value.trim()) {
      return value
    }
    if (Array.isArray(value) && value.length) {
      return value.map(String).join(" ")
    }
  }
  return fallback
}

function initials(name, phone) {
  const source = (name || phone || "?").trim()
  const parts = source.split(/[\s@._+-]+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return source.replace(/\D/g, "").slice(-2) || source.slice(0, 2).toUpperCase()
}

function formatListTime(iso) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  if (isToday(date)) return format(date, "h:mm a")
  if (isYesterday(date)) return "Yesterday"
  if (isThisYear(date)) return format(date, "MMM d")
  return format(date, "MMM d, yyyy")
}

function formatMessageTime(iso) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  return format(date, "MMM d, yyyy · h:mm a")
}

function formatDuration(value) {
  if (!value) return ""
  const raw = String(value)
  const parts = raw.split(":")
  if (parts.length >= 3) {
    const hours = parseInt(parts[0], 10) || 0
    const minutes = parseInt(parts[1], 10) || 0
    const seconds = Math.round(parseFloat(parts[2]) || 0)
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(
        seconds
      ).padStart(2, "0")}`
    }
    return `${minutes}:${String(seconds).padStart(2, "0")}`
  }
  return raw
}

function callerLabel(row) {
  if (row?.candidate_name) {
    return row.candidate_name
  }
  if (row?.contact_name) {
    return row.contact_name
  }
  if (row?.from_phone) {
    return row.from_phone
  }
  return "Unknown caller"
}

function transcriptBody(row) {
  const status = row?.transcription_status
  const text = (row?.transcript || "").trim()
  if (status === "pending") {
    return {
      kind: "pending",
      message: "Transcription in progress…",
    }
  }
  if (status === "failed") {
    return {
      kind: "failed",
      message: "Transcription failed.",
    }
  }
  if (status === "absent") {
    return {
      kind: "absent",
      message: "No transcription available.",
    }
  }
  if (text) {
    return { kind: "text", message: text }
  }
  return {
    kind: "empty",
    message: "No speech detected in this voicemail.",
  }
}

function recordingLabel(row) {
  if (row?.has_recording) {
    return null
  }
  const status = row?.recording_status
  if (status === "in-progress") {
    return "Recording in progress…"
  }
  if (status === "failed") {
    return "Recording unavailable."
  }
  if (status === "absent") {
    return "No recording was captured."
  }
  return "Recording unavailable."
}

export default function VoicemailInboxPanel({ channel, onChannelChange }) {
  const profile = useSelector(selectProfile)
  const axios = useAuthorizedAxiosClient()
  const [searchParams] = useSearchParams()
  const isMobile = useIsMobile()
  const [voicemails, setVoicemails] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [selectedId, setSelectedId] = useState(null)
  const [selectedVoicemail, setSelectedVoicemail] = useState(null)
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const selectedIdRef = useRef(null)

  const concierge = isConcierge(profile)
  const voicemailFromUrl = searchParams.get("voicemail")
  const showList = !isMobile || !selectedId
  const showDetailPane = !isMobile || Boolean(selectedId)

  const loadVoicemails = useCallback(
    (options = {}) => {
      const silent = Boolean(options.silent)
      if (!silent) setIsLoading(true)
      return axios
        .get("/crm/api/inbound-voicemail/inbox/", {
          params: { page_size: 50 },
        })
        .then(({ data }) => {
          setVoicemails(data.results || [])
          setUnreadCount(data.unread_count || 0)
          setError("")
        })
        .catch((err) => {
          setVoicemails([])
          setUnreadCount(0)
          setError(
            firstApiErrorMessage(
              err?.response?.data,
              "Could not load voicemails for this account."
            )
          )
        })
        .finally(() => {
          if (!silent) setIsLoading(false)
        })
    },
    [axios]
  )

  const loadVoicemailDetail = useCallback(
    (voicemailId, options = {}) => {
      const silent = Boolean(options.silent)
      if (!voicemailId) {
        setSelectedVoicemail(null)
        return Promise.resolve()
      }
      return axios
        .get(`/crm/api/inbound-voicemail/inbox/${voicemailId}/`)
        .then(({ data }) => {
          if (selectedIdRef.current !== voicemailId) return
          setSelectedVoicemail(data)
          if (!data.is_read) {
            axios
              .post(`/crm/api/inbound-voicemail/inbox/${voicemailId}/read/`)
              .then(({ data: updated }) => {
                if (selectedIdRef.current !== voicemailId) return
                setSelectedVoicemail(updated)
                setVoicemails((current) =>
                  current.map((row) =>
                    row.id === voicemailId
                      ? { ...row, is_read: true, read_at: updated.read_at }
                      : row
                  )
                )
                setUnreadCount((current) => Math.max(0, current - 1))
              })
              .catch(() => {})
          }
        })
        .catch((err) => {
          if (silent) return
          setError(
            firstApiErrorMessage(
              err?.response?.data,
              "Could not open voicemail."
            )
          )
        })
    },
    [axios]
  )

  useEffect(() => {
    if (!concierge) return undefined
    loadVoicemails()
    const intervalId = window.setInterval(() => {
      loadVoicemails({ silent: true })
    }, VOICEMAIL_LIST_POLL_MS)
    return () => window.clearInterval(intervalId)
  }, [concierge, loadVoicemails, profile?.active_account])

  useEffect(() => {
    if (voicemailFromUrl) {
      const parsed = Number(voicemailFromUrl)
      if (!Number.isNaN(parsed)) {
        setSelectedId(parsed)
      }
    }
  }, [voicemailFromUrl])

  useEffect(() => {
    selectedIdRef.current = selectedId
    loadVoicemailDetail(selectedId)
  }, [loadVoicemailDetail, selectedId])

  if (!concierge) {
    return null
  }

  const voicemailDetailsPath = inboxContactDetailsPath(selectedVoicemail)

  return (
    <ProfileColumn
      sx={{
        gap: 1.25,
        width: "100%",
        minHeight: 0,
        pt: 0.5,
        paddingBlockStart: 1,
        height: {
          xs: "calc(100dvh - 148px)",
          md: `calc(100dvh - ${FOOTER_HEIGHT} - 200px)`,
        },
        maxHeight: {
          xs: "calc(100dvh - 148px)",
          md: `calc(100dvh - ${FOOTER_HEIGHT} - 200px)`,
        },
        overflow: "hidden",
      }}
    >
      <Row
        sx={{
          gap: 2,
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Column sx={{ gap: 0.25, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Messages
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "colors.gray600",
              display: { xs: "none", sm: "block" },
            }}
          >
            Review voicemails left on your concierge calling number.
          </Typography>
        </Column>
        {unreadCount > 0 ? (
          <Chip
            size="small"
            label={`${unreadCount} new`}
            color="primary"
            sx={{ fontWeight: 600 }}
          />
        ) : null}
      </Row>

      <InboxChannelToggle value={channel} onChange={onChannelChange} />

      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          border: "1px solid",
          borderColor: "colors.gray200",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        {showList && (
          <Column
            sx={{
              flex: { xs: "1 1 auto", md: "0 0 340px" },
              width: { md: 340 },
              minWidth: 0,
              minHeight: 0,
              borderRight: { md: "1px solid" },
              borderColor: { md: "colors.gray200" },
              overflow: "auto",
            }}
          >
            {isLoading ? (
              <Column
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  py: 8,
                  gap: 1.5,
                }}
              >
                <CircularProgress size={28} />
                <Typography variant="body2" sx={{ color: "colors.gray500" }}>
                  Loading voicemails…
                </Typography>
              </Column>
            ) : voicemails.length === 0 ? (
              <Column
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  px: 4,
                  py: 8,
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "colors.brand50",
                    color: "primary.main",
                  }}
                >
                  <VoicemailOutlinedIcon />
                </Box>
                <Typography fontWeight={600} color="text.primary">
                  No voicemails yet
                </Typography>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "colors.gray600" }}
                >
                  When callers leave voicemail on your concierge number, they
                  will appear here.
                </Typography>
              </Column>
            ) : (
              voicemails.map((row) => (
                <VoicemailRow
                  key={row.id}
                  row={row}
                  selected={selectedId === row.id}
                  onSelect={() => setSelectedId(row.id)}
                />
              ))
            )}
          </Column>
        )}

        {showDetailPane && (
          <Column
            sx={{
              flex: "1 1 0%",
              minWidth: 0,
              minHeight: 0,
              height: { md: "100%" },
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            {!selectedVoicemail ? (
              <Column
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  px: 4,
                  gap: 1.25,
                }}
              >
                {selectedId ? (
                  <>
                    <CircularProgress size={28} />
                    <Typography
                      variant="body2"
                      sx={{ color: "colors.gray500" }}
                    >
                      Opening voicemail…
                    </Typography>
                  </>
                ) : (
                  <>
                    <VoicemailOutlinedIcon
                      sx={{ fontSize: 40, color: "colors.gray400" }}
                    />
                    <Typography fontWeight={600} color="text.primary">
                      Select a voicemail
                    </Typography>
                    <Typography
                      variant="body2"
                      align="center"
                      sx={{ color: "colors.gray600" }}
                    >
                      Choose a message from the list to listen and read the
                      transcript.
                    </Typography>
                  </>
                )}
              </Column>
            ) : (
              <Column sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <Row
                  sx={{
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "colors.gray200",
                  }}
                >
                  {isMobile ? (
                    <IconButton
                      edge="start"
                      aria-label="Back to voicemail list"
                      onClick={() => setSelectedId(null)}
                    >
                      <ArrowBackOutlinedIcon />
                    </IconButton>
                  ) : null}
                  <Column sx={{ minWidth: 0, flex: 1 }}>
                    {voicemailDetailsPath ? (
                      <Link
                        component={RouterLink}
                        to={voicemailDetailsPath}
                        underline="hover"
                        variant="subtitle1"
                        fontWeight={700}
                        color="text.primary"
                        noWrap
                      >
                        {callerLabel(selectedVoicemail)}
                      </Link>
                    ) : (
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {callerLabel(selectedVoicemail)}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {formatMessageTime(selectedVoicemail.created)}
                      {selectedVoicemail.recording_duration
                        ? ` · ${formatDuration(
                            selectedVoicemail.recording_duration
                          )}`
                        : ""}
                    </Typography>
                  </Column>
                </Row>

                <Column sx={{ px: 2, py: 2, gap: 2 }}>
                  <Row sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    {selectedVoicemail.from_phone ? (
                      <Chip
                        size="small"
                        label={selectedVoicemail.from_phone}
                        variant="outlined"
                      />
                    ) : null}
                    {selectedVoicemail.did_phone ? (
                      <Chip
                        size="small"
                        label={`DID ${selectedVoicemail.did_phone}`}
                        variant="outlined"
                      />
                    ) : null}
                    <InboxContactChip
                      contact={selectedVoicemail}
                      fallbackLabel={selectedVoicemail.from_phone}
                      unmatchedLabel="Unmatched caller"
                    />
                  </Row>

                  <Column sx={{ gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Recording
                    </Typography>
                    {selectedVoicemail.has_recording ? (
                      <VoicemailRecordingPlayer
                        voicemailId={selectedVoicemail.id}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {recordingLabel(selectedVoicemail)}
                      </Typography>
                    )}
                  </Column>

                  <Divider />

                  <Column sx={{ gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Transcription
                    </Typography>
                    <TranscriptionBlock row={selectedVoicemail} />
                  </Column>
                </Column>
              </Column>
            )}
          </Column>
        )}
      </Paper>
    </ProfileColumn>
  )
}

function VoicemailRow({ row, selected, onSelect }) {
  const unread = !row.is_read
  const name = callerLabel(row)
  const phone = row.from_phone
  const showPhone =
    Boolean(phone) &&
    Boolean(row.candidate_name || row.contact_name) &&
    String(phone).toLowerCase() !== String(name || "").toLowerCase()
  const snippet =
    row.transcript_snippet ||
    (row.transcription_status === "pending"
      ? "Transcription in progress…"
      : row.has_recording
      ? "Voicemail recording available"
      : "No recording")

  return (
    <ListItemButton
      selected={selected}
      onClick={onSelect}
      sx={{
        alignItems: "flex-start",
        gap: 1.5,
        px: 2,
        py: 1.75,
        borderBottom: "1px solid",
        borderColor: "colors.gray200",
        bgcolor: selected ? "colors.brand50" : "background.paper",
        borderLeft: "3px solid",
        borderLeftColor: selected
          ? "primary.main"
          : unread
          ? "primary.light"
          : "transparent",
        "&.Mui-selected": {
          bgcolor: "colors.brand50",
        },
        "&.Mui-selected:hover": {
          bgcolor: "colors.brand100",
        },
        "&:hover": {
          bgcolor: selected ? "colors.brand100" : "colors.gray50",
        },
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          mt: 0.25,
          fontSize: 13,
          fontWeight: 700,
          bgcolor: unread ? "primary.main" : "colors.gray200",
          color: unread ? "common.white" : "colors.gray700",
        }}
      >
        {initials(name, phone)}
      </Avatar>
      <Column sx={{ minWidth: 0, flex: 1, gap: 0.35 }}>
        <Row sx={{ alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              flex: showPhone ? "0 1 auto" : 1,
              maxWidth: showPhone ? "46%" : "100%",
              minWidth: 0,
              fontWeight: unread ? 700 : 600,
              color: "text.primary",
            }}
          >
            {name}
          </Typography>
          {showPhone ? (
            <Typography
              variant="caption"
              noWrap
              title={phone}
              sx={{
                flex: 1,
                minWidth: 0,
                color: unread ? "primary.main" : "colors.gray600",
                fontWeight: unread ? 600 : 500,
              }}
            >
              {phone}
            </Typography>
          ) : null}
          <Typography
            variant="caption"
            sx={{
              flexShrink: 0,
              color: unread ? "primary.main" : "colors.gray500",
              fontWeight: unread ? 700 : 500,
            }}
          >
            {formatListTime(row.created)}
          </Typography>
        </Row>
        <Typography
          variant="caption"
          noWrap
          sx={{ color: "colors.gray500", display: "block" }}
        >
          {snippet}
        </Typography>
      </Column>
      {unread ? (
        <Box
          sx={{
            width: 8,
            height: 8,
            mt: 1,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: "primary.main",
          }}
        />
      ) : null}
    </ListItemButton>
  )
}

function TranscriptionBlock({ row }) {
  const body = transcriptBody(row)
  if (body.kind === "text") {
    return (
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {body.message}
      </Typography>
    )
  }
  return (
    <Typography variant="body2" color="text.secondary">
      {body.message}
    </Typography>
  )
}
